/** How long to keep polling after session create before an empty list is treated as final. */
export const SESSION_ROUTINE_RUNS_EXPECT_MS = 90_000;

/** Settled empty fetches during the expectation window before we stop waiting. */
export const SESSION_ROUTINE_RUNS_EMPTY_FETCHES_BEFORE_CLEAR = 3;

const expectationUntilBySession = new Map<string, number>();
const emptyFetchCountBySession = new Map<string, number>();
const listeners = new Set<() => void>();

export const clearSessionExpectingRoutineRuns = (sessionId: string): void => {
  if (
    !expectationUntilBySession.has(sessionId) &&
    !emptyFetchCountBySession.has(sessionId)
  ) {
    return;
  }
  expectationUntilBySession.delete(sessionId);
  emptyFetchCountBySession.delete(sessionId);
  listeners.forEach((listener) => listener());
};

export const markSessionExpectingRoutineRuns = (sessionId: string): void => {
  const until = Date.now() + SESSION_ROUTINE_RUNS_EXPECT_MS;
  expectationUntilBySession.set(sessionId, until);
  emptyFetchCountBySession.delete(sessionId);
  listeners.forEach((listener) => listener());
  window.setTimeout(() => {
    if (expectationUntilBySession.get(sessionId) !== until) return;
    expectationUntilBySession.delete(sessionId);
    emptyFetchCountBySession.delete(sessionId);
    listeners.forEach((listener) => listener());
  }, SESSION_ROUTINE_RUNS_EXPECT_MS + 1);
};

/**
 * Called after a settled session-runs fetch returns no event-rule runs while
 * the post-create expectation window is still open. After a few empty polls,
 * stop showing the "Routine starting…" affordance on sessions that will never
 * get a run.
 */
export const recordSessionRoutineRunsEmptyFetch = (sessionId: string): void => {
  if (!isExpectingSessionRoutineRuns(sessionId)) return;
  const count = (emptyFetchCountBySession.get(sessionId) ?? 0) + 1;
  emptyFetchCountBySession.set(sessionId, count);
  if (count >= SESSION_ROUTINE_RUNS_EMPTY_FETCHES_BEFORE_CLEAR) {
    clearSessionExpectingRoutineRuns(sessionId);
  }
};

/**
 * Call from session-create mutation success (next to sessionCreatedEvent).
 * Keeps the Tasks card visible/polling while event-triggered runs are written.
 * No-op when the session-routine-runs flag is off.
 */
export const onSessionCreatedExpectRoutineRuns = (
  sessionId: string,
  invalidateSessionRuns: (sessionId: string) => void,
  enabled: boolean
): void => {
  if (!enabled) return;
  markSessionExpectingRoutineRuns(sessionId);
  invalidateSessionRuns(sessionId);
};

export const isExpectingSessionRoutineRuns = (sessionId: string | undefined): boolean => {
  if (!sessionId) return false;
  const until = expectationUntilBySession.get(sessionId);
  if (!until) return false;
  if (Date.now() >= until) {
    expectationUntilBySession.delete(sessionId);
    return false;
  }
  return true;
};

export const subscribeSessionRoutineRunsExpectation = (
  listener: () => void
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
